import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { doctorsApi, extractFieldErrors, type ApiUser } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Save, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export function EditDoctor() {
  const navigate = useNavigate();
  const { doctorId } = useParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [formData, setFormData] = useState({ name: '', email: '', password: '', specialization: '', phone: '' });

  useEffect(() => {
    if (!doctorId) return;
    doctorsApi.detail(doctorId).then(({ data }: { data: ApiUser }) => {
      setFormData({
        name: data.full_name,
        email: data.email,
        password: '',
        specialization: data.specialization || '',
        phone: data.phone || '',
      });
    }).catch(() => toast.error('Failed to load doctor')).finally(() => setFetching(false));
  }, [doctorId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!doctorId) return;
    setFieldErrors({});
    setLoading(true);
    try {
      const payload: Record<string, string> = {
        full_name: formData.name,
        email: formData.email,
        specialization: formData.specialization,
        phone: formData.phone,
      };
      if (formData.password) payload.password = formData.password;
      await doctorsApi.update(doctorId, payload);
      toast.success('Doctor updated');
      navigate('/admin');
    } catch (err) {
      const errors = extractFieldErrors(err);
      if (errors) setFieldErrors(errors);
      else toast.error('Failed to update doctor');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="size-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title="Edit Doctor" subtitle="Update doctor information" backTo="/admin" />
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Doctor information</CardTitle>
            <CardDescription>Update account details</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="name">Full name *</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">New password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Leave blank to keep"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                  </div>
                  {fieldErrors.password && <p className="text-xs text-red-600">{fieldErrors.password[0]}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="specialization">Specialization</Label>
                  <Input id="specialization" value={formData.specialization} onChange={(e) => setFormData({ ...formData, specialization: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                </div>
              </div>
              <Button type="submit" disabled={loading}>
                {loading ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Save className="size-4 mr-2" />}
                Save changes
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
